/* ============================================================================
   tab-links.js — die Sprungleiste der Produktseite (<nko-product-tabs>)
   ----------------------------------------------------------------------------
   Frueher assets/nko-tabs.js (seit M6 umbenannt). Geladen von
   sections/product-tab-links.liquid. Ersetzt das fruehere assets/tabs.js.

   Die Leiste kann nicht serverseitig gerendert werden: welche Sections einen
   Eintrag bekommen, steht zwar im Template, aber der Beschriftungstext kommt
   aus den Einstellungen und Uebersetzungen JEDER einzelnen Section — und eine
   Section kann in Liquid nicht in eine andere hineinsehen. Deshalb baut auch
   diese Fassung die Eintraege im Browser.

   WAS ANDERS IST

   1. KEIN LAYOUTSPRUNG. Die Liste kommt leer aus dem Server und wuchs beim
      Fuellen von 0 auf 36px — gemessen, ein sauberer CLS-Beitrag. Die Hoehe
      ist im CSS reserviert (`.product-tabs { min-height }`).

   2. KEIN SCROLL-LISTENER. Das Original haengte einen ungedrosselten
      `window.scroll`-Handler an, der bei JEDEM Frame ueber alle Sections
      iterierte, fuer jede `getBoundingClientRect()` aufrief (erzwingt Layout)
      und zusaetzlich ein `querySelector` pro Section machte. Hier macht das
      ein IntersectionObserver, der nur bei tatsaechlichen Uebergaengen feuert.

   3. KEIN SCROLL-JS. Die Eintraege sind echte `<a href="#section-id">`. Das
      Springen macht der Browser; der Versatz kommt aus `scroll-margin-top`
      im CSS, das weiche Scrollen aus `scroll-behavior`. Damit
      funktioniert die Leiste auch, wenn dieses Skript nicht laedt — und sie
      respektiert `prefers-reduced-motion`, was die JS-Fassung nicht tat.

   4. OHNE `document.querySelector(".header").style.display = "block"`.
      Das stand im Original ganz oben und war ein Notnagel gegen eine
      Vorabblende, die es nicht mehr gibt. Auf einer Seite ohne `.header`
      warf die Zeile. Nebenbei hob sie unter 1200px das Ausblenden des Kopfs
      beim Runterscrollen auf — die Leiste klebt dort bei 56px, direkt unter
      ihm. Das regelt jetzt header.css §5 (`:has(.tabs--sticky)`), ohne JS.

   5. OHNE das `console.log(selected)` im Klick-Handler.

   6. DER AKTIVE EINTRAG WIRD NICHT MEHR GERATEN. Das Original setzte
      `is-selected` hart auf `index == 2` — also auf die dritte Section,
      unabhaengig davon, ob die ueberhaupt einen Tab hat. Jetzt setzt der
      Observer den Zustand aus der Sichtbarkeit.

   VERTRAG: `.product-tabs li`, `li[data-tab]`, `li.is-selected` und
   `a.scroll-link` bleiben — das CSS der Leiste stylt darauf.

   AUF- UND ABBAU: Alles, was `connectedCallback` anhaengt, haengt an EINEM
   AbortController bzw. am Observer; `disconnectedCallback` raeumt beides ab
   und leert die Liste. Haengt der Editor die Section neu ein, entstehen die
   Eintraege damit nicht doppelt.
   ==========================================================================  */

class NkoProductTabs extends HTMLElement {
  connectedCallback() {
    this.liste = this.querySelector("ul.product-tabs");
    if (!this.liste) return;

    this.hoerer = new AbortController();

    // Dieselbe Menge wie im Original: die Sections des Hauptinhalts.
    const alle = [
      ...document.querySelectorAll(".product-page #MainContent .shopify-section"),
    ];

    /* Zwei Zuordnungen, beide in Dokumentreihenfolge:
         zuLi       Section MIT Tab -> ihr Eintrag (fuer die Leiste selbst)
         zuordnung  JEDE Section ab dem ersten Tab -> der zuletzt gesehene
                    Eintrag
       Die zweite ist fuer die Markierung. „Kundenbewertungen“ ist eine 0px hohe
       Tab-Section (custom_app_…); das Widget steht in den FOLGENDEN Sections,
       die kein `data-tab` tragen. Beobachtet wurden bisher nur Tab-Sections —
       mitten im Widget war deshalb „Zubehoer“ oder „Glaeser“ markiert, nach
       einem Direktsprung sogar „Ueberblick“. */
    this.zuLi = new Map();
    this.zuordnung = new Map();
    let letztesLi = null;
    for (const abschnitt of alle) {
      const tab = abschnitt.id ? abschnitt.querySelector("[data-tab]") : null;
      const text = tab ? tab.getAttribute("data-tab") : "";
      if (text) {
        const li = document.createElement("li");
        li.setAttribute("data-tab", text);
        const a = document.createElement("a");
        a.className = "scroll-link";
        a.href = `#${abschnitt.id}`;
        a.textContent = text;
        li.appendChild(a);
        this.liste.appendChild(li);
        this.zuLi.set(abschnitt, li);
        letztesLi = li;
      }
      if (letztesLi) this.zuordnung.set(abschnitt, letztesLi);
    }

    if (!this.zuLi.size) return;

    // Erster Eintrag aktiv, bis der Observer etwas Besseres weiss.
    this.setzeAktiv(this.zuLi.values().next().value);

    // Ein Klick markiert sofort — der Observer zieht beim Ankommen nach.
    this.liste.addEventListener(
      "click",
      (e) => {
        const li = e.target.closest("li[data-tab]");
        if (li) this.setzeAktiv(li);
      },
      { signal: this.hoerer.signal }
    );

    this.beobachte();
  }

  disconnectedCallback() {
    this.hoerer?.abort();
    this.observer?.disconnect();
    this.observer = null;
    this.aktiv = null;
    // Die Eintraege baut connectedCallback aus dem Dokument neu.
    this.liste?.replaceChildren();
  }

  setzeAktiv(li) {
    if (!li || li === this.aktiv) return;
    this.aktiv?.classList.remove("is-selected");
    li.classList.add("is-selected");
    this.aktiv = li;
  }

  beobachte() {
    /* Der Beobachtungsbereich wird oben um die Hoehe der klebenden Leiste
       gekuerzt — was darueber liegt, ist verdeckt und soll nicht zaehlen.
       Unten wird NICHT gekuerzt.

       Das ist Absicht: eine Kuerzung von beiden Seiten (etwa
       `-300px 0px -60% 0px`) laesst bei 900px Fensterhoehe ein 60px schmales
       Band uebrig — und auf einem flachen Fenster, etwa einem Handy im
       Querformat mit 500px, wird es NEGATIV. Dann meldet der Observer nie
       etwas, und die Markierung bleibt fuer immer auf dem ersten Eintrag
       stehen. Ein Fehler, der nur auf kleinen Geraeten auftritt.

       Stattdessen wird gemerkt, WELCHE Abschnitte gerade sichtbar sind, und
       markiert wird der erste davon in Dokumentreihenfolge — also der
       oberste, der nicht mehr unter der Leiste liegt. Das kann nicht
       kollabieren. */
    this.sichtbar = new Set();
    const linie = this.oberkante();

    this.observer = new IntersectionObserver(
      (eintraege) => {
        for (const e of eintraege) {
          if (e.isIntersecting) this.sichtbar.add(e.target);
          else this.sichtbar.delete(e.target);
        }
        for (const abschnitt of this.zuordnung.keys()) {
          if (this.sichtbar.has(abschnitt)) {
            this.setzeAktiv(this.zuordnung.get(abschnitt));
            return;
          }
        }
        // Nichts sichtbar (etwa eine hohe Section ohne Uebergang): der letzte
        // Abschnitt, der schon ueber der Beobachtungslinie liegt. Nur in diesem
        // seltenen Zweig wird gemessen.
        let darueber = null;
        for (const abschnitt of this.zuordnung.keys()) {
          if (abschnitt.getBoundingClientRect().top < linie) darueber = abschnitt;
          else break;
        }
        if (darueber) this.setzeAktiv(this.zuordnung.get(darueber));
      },
      { rootMargin: `-${linie}px 0px 0px 0px`, threshold: 0 }
    );

    for (const abschnitt of this.zuordnung.keys()) this.observer.observe(abschnitt);
  }

  /* Wo beginnt ein Abschnitt, nachdem man ihn angesprungen hat? Genau bei
     seinem `scroll-margin-top` — das ist der Wert, mit dem das CSS ihn unter
     der klebenden Leiste absetzt. Der Beobachtungsbereich muss oben um
     denselben Betrag gekuerzt werden, sonst zeigt die Markierung daneben.

     VORHER STAND HIER ETWAS ANDERES, und es war zweifach falsch:

       return hoehe + Math.max(0, parseFloat(getComputedStyle(this).top) || 0)

     1. Die Leiste ist an der Stelle, an der gemessen wurde, `position: static`
        — `top` ist dann `auto`, `parseFloat('auto')` ist NaN, und `|| 0`
        machte daraus 0. Der Sticky-Versatz floss nie ein.
     2. Selbst mit ihm waeren es 77 + 90 = 167px gewesen, das
        `scroll-margin-top` betraegt aber 200px. Der VORHERIGE Abschnitt ragte
        damit noch bis y=160 in den beobachteten Bereich und gewann, weil er in
        Dokumentreihenfolge frueher kommt.

     Gemessen: die Markierung hinkte dadurch durchgaengig einen Eintrag
     hinterher — Sprung auf "Details" markierte "Beschreibung".

     Das eine Pixel obendrauf ist kein Schoenheitsfehler, sondern noetig: nach
     einem Sprung liegt die Unterkante des VORHERIGEN Abschnitts exakt auf der
     Oberkante des Bereichs. Bei genauer Beruehrung melden Browser die
     Ueberschneidung noch — und der vorherige Abschnitt gewinnt, weil er in
     Dokumentreihenfolge frueher kommt. Gemessen am letzten Eintrag
     ("Kundenbewertungen" markierte "Zubehoer"). Ein Pixel Abstand entscheidet
     den Gleichstand.

     Der Rueckfall auf 200 ist der Wert aus dem CSS; er greift nur, wenn
     gar kein Abschnitt beobachtet wird.

     DER LETZTE EINTRAG („Kundenbewertungen“) hing bis hierher trotzdem
     hinterher: seine Section ist 0px hoch, das reviews.io-Widget steht in den
     Folge-Sections ohne `data-tab`. Geloest ueber `zuordnung` (siehe
     `connectedCallback`): diese Sections zaehlen jetzt zum letzten Eintrag
     davor und werden mitbeobachtet. */
  oberkante() {
    const erster = this.zuLi.keys().next().value;
    const wert = erster ? parseFloat(getComputedStyle(erster).scrollMarginTop) : NaN;
    const rand = Number.isFinite(wert) && wert > 0 ? Math.round(wert) : 200;
    return rand + 1;
  }
}

if (!customElements.get("nko-product-tabs")) {
  customElements.define("nko-product-tabs", NkoProductTabs);
}

/* ----------------------------------------------------------------------------
   NUR ALS MODUL LADEN: <script src="…" type="module">, nie mit `defer`.
   Module laufen wie `defer` nach dem Parsen in Dokumentreihenfolge, aber nur
   einmal je URL und im strict mode.
   ---------------------------------------------------------------------------- */
